import { PERMISSION_MODULE_LABELS } from '@crm/shared';
import { Badge, Button, Card, CardContent, Input, Spinner } from '@crm/ui';
import { ChevronRight, Lock, Pencil, Plus, ShieldCheck, Trash2, UsersRound } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ConfirmDialog, EmptyState, LOAD_MORE_STEP, LoadMore, PageHeader } from '@/components';
import { useDebounce, useDocumentTitle } from '@/hooks';
import { useCan } from '@/modules/auth/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import { PROFILES_ROUTES } from '../constants';
import { useDeleteProfile, useProfiles } from '../hooks';
import type { PublicProfile } from '../types';

/** "Clientes, Agenda +2" - o que o perfil alcanca, sem despejar a matriz. */
function resumo(profile: PublicProfile): string {
  const modulos = profile.permissions.map((entry) => PERMISSION_MODULE_LABELS[entry.module]);

  if (modulos.length === 0) return 'Sem acesso a nenhum modulo';

  return modulos.length <= 3
    ? modulos.join(', ')
    : `${modulos.slice(0, 3).join(', ')} +${modulos.length - 3}`;
}

export function ProfilesPage() {
  useDocumentTitle('Perfis de acesso');

  const canWrite = useCan('profiles', 'WRITE');

  const [perPage, setPerPage] = useState(LOAD_MORE_STEP);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const profiles = useProfiles({ page: 1, perPage, search: debouncedSearch });
  const deleteProfile = useDeleteProfile();

  const meta = profiles.data?.meta;

  const [pendingRemoval, setPendingRemoval] = useState<{ id: string; name: string } | null>(null);

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<ShieldCheck />}
        title="Perfis de acesso"
        description="Cada perfil e um conjunto de permissoes; o usuario recebe um ou mais perfis."
        actions={
          canWrite ? (
            <Button asChild>
              <Link to={PROFILES_ROUTES.NEW}>
                <Plus className="h-4 w-4" />
                <span>
                  Novo<span className="hidden sm:inline"> perfil</span>
                </span>
              </Link>
            </Button>
          ) : null
        }
      />

      {deleteProfile.error ? (
        <p className="text-sm text-destructive">{toErrorMessage(deleteProfile.error)}</p>
      ) : null}

      <Card variant="flush">
        {/* Filtro e lista no mesmo quadro: sao a mesma ferramenta. */}
        <CardContent className="space-y-6">
          <Input
            className="max-w-xs"
            placeholder="Buscar por nome"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPerPage(LOAD_MORE_STEP);
            }}
          />

          {profiles.isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner className="h-6 w-6 text-muted-foreground" />
            </div>
          ) : profiles.isError ? (
            <p className="text-sm text-destructive">{toErrorMessage(profiles.error)}</p>
          ) : profiles.data && profiles.data.data.length > 0 ? (
            <ul className="divide-y divide-border">
              {profiles.data.data.map((profile) => (
                <li
                  key={profile.id}
                  // Acoes a direita, na mesma linha do nome: empilhadas, cada
                  // perfil gastava duas alturas no celular.
                  className="flex items-center gap-2 py-3 first:pt-0 last:pb-0 sm:gap-4"
                >
                  {/* A linha toda abre o perfil: no celular o alvo e o
                      registro inteiro, nao um icone no canto. */}
                  <Link to={PROFILES_ROUTES.detail(profile.id)} className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{profile.name}</span>
                      {profile.isSystem ? (
                        <Badge variant="secondary" className="gap-1">
                          <Lock className="h-3 w-3" />
                          Do sistema
                        </Badge>
                      ) : null}
                      <Badge variant="outline">
                        {profile.usersCount} {profile.usersCount === 1 ? 'usuario' : 'usuarios'}
                      </Badge>
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {resumo(profile)}
                    </span>
                  </Link>

                  <ChevronRight
                    aria-hidden
                    className="h-5 w-5 shrink-0 text-muted-foreground sm:hidden"
                  />

                  {canWrite ? (
                    <div className="hidden shrink-0 items-center sm:flex sm:justify-end">
                      <Button variant="ghost" size="icon" title="Editar" asChild>
                        <Link to={PROFILES_ROUTES.detail(profile.id)}>
                          <Pencil className="h-4 w-4" />
                        </Link>
                      </Button>

                      {/* Perfil do sistema existe em toda empresa: da para
                          ajustar as permissoes, nunca excluir. */}
                      <Button
                        variant="ghost"
                        size="icon"
                        title={
                          profile.isSystem
                            ? 'Perfil do sistema nao pode ser excluido'
                            : 'Excluir perfil'
                        }
                        disabled={profile.isSystem}
                        onClick={() => setPendingRemoval({ id: profile.id, name: profile.name })}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState icon={<UsersRound />} title="Nenhum perfil encontrado" />
          )}

          <LoadMore
            meta={meta}
            label="perfis"
            loading={profiles.isFetching}
            onLoadMore={() => setPerPage((atual) => atual + LOAD_MORE_STEP)}
          />
        </CardContent>
      </Card>

      <ConfirmDialog
        open={pendingRemoval !== null}
        title="Excluir perfil"
        message={`O perfil ${pendingRemoval?.name ?? ''} sai do sistema. Usuarios que dependem so dele ficam sem acesso.`}
        confirmLabel="Excluir"
        loading={deleteProfile.isPending}
        onClose={() => setPendingRemoval(null)}
        onConfirm={() => {
          if (!pendingRemoval) return;

          deleteProfile.mutate(pendingRemoval.id, { onSuccess: () => setPendingRemoval(null) });
        }}
      />
    </div>
  );
}
